import { useApi } from '@/api/api';
import {
  authClient,
  readAdminRole,
  useAuthSession,
  type AdminRole,
  type SessionPayload,
} from '@/lib/auth-client';
import { setSentryUser } from '@/lib/sentry';
import axios from 'axios';
import { defineStore } from 'pinia';
import { watch } from 'vue';

/**
 * Reactive session singleton. Subscribing at module scope keeps the
 * Better Auth nanostore alive for the whole app lifetime; the cookie
 * is the single source of truth (nothing persisted to localStorage).
 */
const sessionContext = useAuthSession();

// Migration: drop the legacy persisted store (contained the session token).
if (typeof localStorage !== 'undefined') {
  localStorage.removeItem('authStore');
}

let sessionReadyPromise: Promise<void> | null = null;

/**
 * Fetch the session once, memoized for concurrent callers (router guard + bootstrap).
 *
 * The nanostore also auto-fetches on first subscription; when both requests
 * race, one can be aborted and leave the state momentarily empty. Retry a
 * few times so the guard sees the settled session instead of bouncing to
 * login on a stale null.
 */
function ensureSessionFetched(): Promise<void> {
  sessionReadyPromise ??= (async () => {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      await sessionContext.value.refetch();
      if (sessionContext.value.data !== null) return;
      await new Promise((resolve) => setTimeout(resolve, 150));
    }
  })();
  return sessionReadyPromise;
}

/** Drop the memoized fetch so the next call re-reads the session (login/logout/expiry). */
function invalidateSessionFetch() {
  sessionReadyPromise = null;
}

// Mirror session identity into Sentry (id + role only). Local profile
// cleanup happens in signOut()/handleSessionExpired().
watch(
  () => sessionContext.value.data,
  (data) => {
    setSentryUser(data ? { id: data.user.id, role: readAdminRole(data.user) ?? undefined } : null);
  },
);

export const useAuthStore = defineStore('authStore', {
  state: () => ({
    /** Staff profile (from GET /admin/profile), used for display purposes. */
    profile: null as Profile | null,
    /** True once the profile lookup ran (success or 404) — avoids refetch loops. */
    profileLoaded: false,
    loading: false,
    error: null as string | null,
  }),

  getters: {
    isLoggedIn: () => sessionContext.value.data !== null,

    /** Raw Better Auth session payload (session + user). */
    session: (): SessionPayload => sessionContext.value.data,

    /** Role of the signed-in staff user, from the session (no extra request needed). */
    role: (): AdminRole | null => readAdminRole(sessionContext.value.data?.user),
  },

  actions: {
    /** Login via email/password — Better Auth sets the session cookie. */
    async signIn(email: string, password: string): Promise<boolean> {
      this.loading = true;
      this.error = null;
      try {
        const { error } = await authClient.signIn.email({ email, password });
        if (error) {
          this.error = error.message || 'Invalid credentials';
          return false;
        }
        // The memoized boot-time fetch may have settled before login —
        // force a fresh fetch so `isLoggedIn` reflects the new session.
        invalidateSessionFetch();
        await ensureSessionFetched();
        // Don't block the redirect on the profile lookup; it populates after.
        void this.getProfile();
        return true;
      } catch (err: unknown) {
        this.error = extractAuthErrorMessage(err) || 'Invalid credentials';
        return false;
      } finally {
        this.loading = false;
      }
    },

    async requestPasswordReset(email: string, redirectTo: string): Promise<boolean> {
      this.loading = true;
      this.error = null;
      try {
        const { error } = await authClient.requestPasswordReset({ email, redirectTo });
        if (error) {
          this.error = error.message || 'Invalid email';
          return false;
        }
        return true;
      } catch (err: unknown) {
        this.error = extractAuthErrorMessage(err) || 'Unknown error';
        return false;
      } finally {
        this.loading = false;
      }
    },

    async resetPassword(newPassword: string, token: string): Promise<boolean> {
      this.loading = true;
      this.error = null;
      try {
        const { error } = await authClient.resetPassword({ newPassword, token });
        if (error) {
          this.error = error.message || 'Incorrect password';
          return false;
        }
        return true;
      } catch (err: unknown) {
        this.error = extractAuthErrorMessage(err) || 'Unknown error';
        return false;
      } finally {
        this.loading = false;
      }
    },

    async changePassword(currentPassword: string, newPassword: string): Promise<boolean> {
      this.loading = true;
      this.error = null;
      try {
        const { error } = await authClient.changePassword({ currentPassword, newPassword });
        if (error) {
          this.error = error.message || 'Incorrect password';
          return false;
        }
        return true;
      } catch (err: unknown) {
        this.error = extractAuthErrorMessage(err) || 'Unknown error';
        return false;
      } finally {
        this.loading = false;
      }
    },

    /** Get staff profile for display (names, contact info). */
    async getProfile() {
      const { axios } = useApi();
      try {
        const { data } = await (await axios()).get('/admin/profile');
        this.profile = data;
      } catch {
        this.profile = null;
      } finally {
        this.profileLoaded = true;
      }
    },

    /* Update staff profile — syncs the custom Profile entity. */
    async updateProfile(firstname: string, lastname: string): Promise<boolean> {
      this.loading = true;
      this.error = null;
      try {
        const { axios } = useApi();
        await (await axios()).patch('/admin/profile', { firstname, lastname });
        if (this.profile) {
          this.profile.firstname = firstname;
          this.profile.lastname = lastname;
        }
        return true;
      } catch (err: unknown) {
        this.error = err instanceof Error ? err.message : 'Invalid data';
        return false;
      } finally {
        this.loading = false;
      }
    },

    /** Logout (destroys server session + clears local state). */
    async signOut(): Promise<boolean> {
      try {
        await authClient.signOut();
      } catch {
        // ignore server errors — clear local state regardless
      } finally {
        this.profile = null;
        this.profileLoaded = false;
        invalidateSessionFetch();
      }
      return true;
    },

    /**
     * Restore login state at app start. Memoized — safe to call from
     * both the router guard and bootstrap. Also backfills the staff
     * profile once a session is confirmed, without blocking navigation.
     */
    init() {
      const ready = ensureSessionFetched();
      void ready.then(() => {
        if (sessionContext.value.data !== null && !this.profileLoaded) {
          void this.getProfile();
        }
      });
      return ready;
    },

    /** Called by the axios interceptor when an API call returns 401. */
    handleSessionExpired() {
      this.profile = null;
      this.profileLoaded = false;
      invalidateSessionFetch();
      void ensureSessionFetched();
    },
  },
});

function extractAuthErrorMessage(err: unknown): string | null {
  if (axios.isAxiosError(err)) {
    const message = (err.response?.data as { message?: string } | undefined)?.message;
    return message ?? null;
  }
  if (err instanceof Error && err.message) return err.message;
  return null;
}

type User = {
  id: string;
  name: string;
  email: string;
  role: string;
  image: string | null;
  phoneNumber: string | null;
  profileId: string;
  createdAt: string;
  updatedAt: string;
  emailVerified: boolean;
  phoneNumberVerified: boolean;
};

type Profile = {
  id: string;
  firstname: string | null;
  lastname: string | null;
  userId: string;
  user: User;
};
